import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { CustomChemicalService, CustomChemical } from '../../../../core/services/custom-chemical.service';

@Component({
  selector: 'app-custom-chemicals',
  standalone: true,
  imports: [CommonModule, ConfirmModalComponent],
  templateUrl: './custom-chemicals.component.html',
  styleUrls: ['./custom-chemicals.component.scss']
})
export class CustomChemicalsComponent implements OnInit {
  @ViewChild('confirmModal') confirmModal!: ConfirmModalComponent;

  private customChemicalService = inject(CustomChemicalService);
  private router = inject(Router);

  // Use service signals
  customChemicals = this.customChemicalService.chemicals;
  isLoading = this.customChemicalService.isLoading;
  error = this.customChemicalService.error;

  chemicalToDelete: CustomChemical | null = null;

  ngOnInit(): void {
    this.loadCustomChemicals();
  }

  async loadCustomChemicals(): Promise<void> {
    await this.customChemicalService.loadChemicals();
  }

  addCustomChemical(): void {
    this.router.navigate(['/logbook/chemicals/custom/add']);
  }

  viewChemical(chemical: CustomChemical): void {
    this.router.navigate(['/logbook/chemicals/custom', chemical.id, 'view']);
  }

  editChemical(chemical: CustomChemical): void {
    this.router.navigate(['/logbook/chemicals/custom', chemical.id, 'edit']);
  }

  deleteChemical(chemical: CustomChemical): void {
    this.chemicalToDelete = chemical;
    this.confirmModal.open({
      title: 'Delete Custom Chemical',
      message: `Are you sure you want to delete "${chemical.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonClass: 'btn-danger',
      icon: 'bi-trash'
    });
  }

  async onDeleteConfirmed(): Promise<void> {
    if (this.chemicalToDelete) {
      const success = await this.customChemicalService.deleteChemical(this.chemicalToDelete.id);
      if (success) {
        console.log('Custom chemical deleted successfully');
      }
      this.chemicalToDelete = null;
    }
  }

  onDeleteCancelled(): void {
    this.chemicalToDelete = null;
  }

  async duplicateChemical(chemical: CustomChemical): Promise<void> {
    // Create a copy with "(Copy)" appended to name
    const duplicate = {
      name: `${chemical.name} (Copy)`,
      epaNumber: chemical.epaNumber,
      manufacturer: chemical.manufacturer,
      activeIngredient: chemical.activeIngredient,
      signalWord: chemical.signalWord,
      category: chemical.category,
      formulation: chemical.formulation,
      rei: chemical.rei,
      phi: chemical.phi,
      defaultRate: chemical.defaultRate,
      targetPests: chemical.targetPests,
      crops: chemical.crops,
      notes: chemical.notes
    };

    const result = await this.customChemicalService.createChemical(duplicate);
    if (result) {
      console.log('Custom chemical duplicated successfully');
    }
  }

  getCategoryBadgeClass(category?: string): string {
    switch (category) {
      case 'herbicide':
        return 'bg-success-subtle text-success';
      case 'insecticide':
        return 'bg-danger-subtle text-danger';
      case 'fungicide':
        return 'bg-primary-subtle text-primary';
      case 'fertilizer':
        return 'bg-warning-subtle text-warning-emphasis';
      default:
        return 'bg-secondary-subtle text-secondary';
    }
  }

  getCategoryLabel(category?: string): string {
    if (!category) return 'Other';
    return category.charAt(0).toUpperCase() + category.slice(1);
  }
}
